import React, { useEffect, useMemo, useState } from "react";
// 🆕 Import the audience API service for segment data
import audienceApiService from "../services/audienceApiService";
import { relevanceFromDistance } from "../utils";
import {
	filterAudiencesInCategory,
	getCategoryInfo,
	getCategoryStats,
	groupAudiencesByCategory,
} from "./audienceCategorization";

const formatNumber = (value) => {
	if (value === null || value === undefined || value === "") return "N/A";
	if (typeof value === "number") {
		return value.toLocaleString();
	}
	if (typeof value === "string" && !isNaN(value)) {
		return parseInt(value).toLocaleString();
	}
	return value;
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === "") return "N/A";
	if (typeof value === "number") {
		return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	}
	return value;
};

const formatPercentage = (value) => {
	if (value === null || value === undefined || value === "") return "N/A";
	if (typeof value === "number") {
		return `${value.toFixed(2)}%`;
	}
	return value;
};

// Clean Minimal Modal Component with Rich Data Display and Segment Tab
export const AudienceDetailModal = ({ audience, isOpen, onClose }) => {
	const [activeTab, setActiveTab] = useState("performance");
	// 🆕 Segment data state
	const [segmentData, setSegmentData] = useState(null);
	const [segmentLoading, setSegmentLoading] = useState(false);
	const [segmentError, setSegmentError] = useState(null);

	// 🆕 Fetch segment data when modal opens and segments tab is accessed
	const fetchSegmentData = async () => {
		const audienceId =
			audience?.audienceVector?.audienceId ||
			audience?.audienceId ||
			audience?.id;

		if (!audienceId) {
			setSegmentError("No audience ID available");
			return;
		}

		setSegmentLoading(true);
		setSegmentError(null);

		try {
			console.log("🧩 Fetching segments for audience ID:", audienceId);
			const response = await audienceApiService.getAudienceSegments(audienceId);

			if (response.success) {
				console.log("✅ Segment data fetched successfully:", response.data);
				setSegmentData(response.data);
			} else {
				console.error("❌ Failed to fetch segment data:", response.error);
				setSegmentError(response.error || "Failed to fetch segment data");
			}
		} catch (error) {
			console.error("❌ Error fetching segment data:", error);
			setSegmentError(
				error.message || "An error occurred while fetching segment data",
			);
		} finally {
			setSegmentLoading(false);
		}
	};

	// 🆕 Auto-fetch segments when segments tab is clicked
	useEffect(() => {
		if (
			isOpen &&
			activeTab === "segments" &&
			!segmentData &&
			!segmentLoading &&
			audience
		) {
			fetchSegmentData();
		}
	}, [activeTab, audience, isOpen, segmentData, segmentLoading]);

	// 🆕 Reset segment data when modal closes or audience changes
	useEffect(() => {
		if (!isOpen) {
			setSegmentData(null);
			setSegmentError(null);
			setActiveTab("performance");
		}
	}, [isOpen]);

	useEffect(() => {
		// Reset segment data when audience changes
		if (audience?.audienceVector?.audienceId) {
			setSegmentData(null);
			setSegmentError(null);
		}
	}, [audience?.audienceVector?.audienceId]);

	// Early return after all hooks
	if (!isOpen || !audience) return null;

	const formatNumber = (value) => {
		const num = Number(value) || 0;
		return num === 0 ? "0.00" : num.toFixed(2);
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		const num = Number(value) || 0;
		if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
		if (num < 1) return `$${num.toFixed(5)}`;
		return `$${num.toFixed(2)}`;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return `${value.toFixed(2)}%`;
		}
		return value;
	};

	const formatNumberWithCommas = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		const num = Number(value) || 0;
		if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toLocaleString();
	};

	// 🆕 Updated tabs - only 3 tabs now
	const tabs = [
		{ id: "performance", label: "Performance & Scale", icon: "📊" },
		{ id: "metadata", label: "Audience Details", icon: "👥" },
		{ id: "segments", label: "Segment IDs", icon: "🧩" },
	];

	const renderTabContent = () => {
		const metrics = audience.audienceMetrics || {};
		const vector = audience.audienceVector || {};

		switch (activeTab) {
			case "performance":
				return (
					<div className="tab-content">
						{/* Updated Key Metrics - New metrics as requested */}
						<div className="section">
							<h4>Key Performance Indicators</h4>
							<div className="metric-grid-5">
								<div className="metric-card">
									<div className="metric-value">
										{formatNumberWithCommas(metrics.uniqueUsers)}
									</div>
									<div className="metric-label">Reach</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatNumberWithCommas(metrics.impressions)}
									</div>
									<div className="metric-label">Impressions</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{metrics.dealIds?.length || 0}
									</div>
									<div className="metric-label">No. of Deals</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatCurrency(metrics.spendInUsd / 1000000)}
									</div>
									<div className="metric-label">Spend</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{metrics.reportMonths} Months
									</div>
									<div className="metric-label">Report Month</div>
								</div>
							</div>

							{/* Add small tags for relevance and composite scores */}
							<div
								style={{
									marginTop: "12px",
									display: "flex",
									gap: "8px",
									justifyContent: "center",
								}}
							>
								<div className="score-tag">
									<span className="score-label">Relevance:</span>
									<span className="score-value">
										{audience.relevanceScore?.toFixed(1) ||
											formatNumber(audience.score * 100)}
										%
									</span>
								</div>
								<div className="score-tag">
									<span className="score-label">Composite:</span>
									<span className="score-value">
										{audience.compositeScore?.toFixed(1) || "N/A"}
									</span>
								</div>
							</div>
						</div>

						{/* Performance Metrics */}
						<div className="section">
							<h4>Performance Metrics</h4>
							<div className="metric-grid-3">
								<div className="metric-group">
									<div className="group-title">Engagement</div>
									<div className="metric-row">
										<span>CTR</span>
										<span>{formatPercentage(metrics.clickThroughRate)}</span>
									</div>
									<div className="metric-row">
										<span>Viewability</span>
										<span>{formatPercentage(metrics.viewability)}</span>
									</div>
									<div className="metric-row">
										<span>Clicks</span>
										<span>{formatNumberWithCommas(metrics.clicks)}</span>
									</div>
									<div className="metric-row">
										<span>Video Completions</span>
										<span>
											{formatNumberWithCommas(metrics.completeFirstOccurrence)}
										</span>
									</div>
								</div>

								<div className="metric-group">
									<div className="group-title">Revenue</div>
									<div className="metric-row">
										<span>Average eCPM</span>
										<span>{formatCurrency(metrics.avgEcpm)}</span>
									</div>
									<div className="metric-row">
										<span>eCPM Range</span>
										<span>
											{formatCurrency(metrics.minEcpm)} -{" "}
											{formatCurrency(metrics.maxEcpm)}
										</span>
									</div>
									<div className="metric-row">
										<span>Spend Per User</span>
										<span>
											{formatCurrency(Number(metrics.avgSpendPerUniqueUser))}
										</span>
									</div>
									<div className="metric-row">
										<span>Publisher Revenue</span>
										<span>
											{formatCurrency(
												metrics.cprmarketPublisherRevenueInUsd / 1000000,
											)}
										</span>
									</div>
								</div>

								<div className="metric-group">
									<div className="group-title">Scale</div>
									<div className="metric-row">
										<span>Total Requests</span>
										<span>{formatNumberWithCommas(metrics.requests)}</span>
									</div>
									<div className="metric-row">
										<span>Addressable Reach</span>
										<span>
											{formatNumberWithCommas(metrics.addressableReach)}
										</span>
									</div>
									<div className="metric-row">
										<span>Cookies</span>
										<span>{formatNumberWithCommas(metrics.cookies)}</span>
									</div>
									<div className="metric-row">
										<span>ID5 Identifiers</span>
										<span>{formatNumberWithCommas(metrics.id5s)}</span>
									</div>
								</div>
							</div>
						</div>

						{/* Device Distribution */}
						<div className="section">
							<h4>Device Distribution</h4>
							<div className="metric-grid-4">
								<div className="device-card">
									<div className="device-icon">📱</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.mobileDevices)}
									</div>
									<div className="device-label">Mobile</div>
								</div>
								<div className="device-card">
									<div className="device-icon">💻</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.desktopsWithRequests)}
									</div>
									<div className="device-label">Desktop</div>
								</div>
								<div className="device-card">
									<div className="device-icon">📋</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.tabletDevices)}
									</div>
									<div className="device-label">Tablet</div>
								</div>
								<div className="device-card">
									<div className="device-icon">📺</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.connectedTvDevices)}
									</div>
									<div className="device-label">Connected TV</div>
								</div>
							</div>
						</div>
					</div>
				);

			case "metadata":
				return (
					<div className="tab-content">
						{/* Basic Information */}
						<div className="section">
							<h4>Basic Information</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Audience Name</span>
									<span className="info-value">{vector.audienceName}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Audience ID</span>
									<span className="info-value code">{vector.audienceId}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Account</span>
									<span className="info-value">{vector.accountName}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Strategy</span>
									<span className="info-value">{vector.audienceStrategy}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Primary Persona</span>
									<span className="info-value">{vector.primaryPersona}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Data Source</span>
									<span className="info-value">{vector.dataSource}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Region</span>
									<span className="info-value">{vector.region}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Export Type</span>
									<span className="info-value">{vector.exportType}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Status</span>
									<span className="info-value">{vector.status}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Report Status</span>
									<span className="info-value">{vector.reportStatus}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Created With</span>
									<span className="info-value">{vector.createdWith}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Shareable</span>
									<span className="info-value">
										{vector.shareableOpenxAudience ? "Yes" : "No"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Report Duration</span>
									<span className="info-value">
										{metrics.reportMonths} months
									</span>
								</div>
							</div>
							{vector.notesDescription && (
								<div className="description">
									<span className="info-label">Description</span>
									<p>{vector.notesDescription}</p>
								</div>
							)}
						</div>

						{/* Performance Insights */}
						<div className="section">
							<h4>Performance Insights</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Win Rate (OX)</span>
									<span className="info-value">
										{formatPercentage(metrics.oxWinRate)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Publisher Win Rate</span>
									<span className="info-value">
										{formatPercentage(metrics.pubWinRate)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Bid CPM</span>
									<span className="info-value">
										{formatCurrency(metrics.bidCpm / 1000000)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Bid Guidance</span>
									<span className="info-value">
										{formatCurrency(metrics.bidGuidance / 1000000)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Total Fills</span>
									<span className="info-value">
										{formatNumberWithCommas(metrics.fills)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Non-Zero Bids</span>
									<span className="info-value">
										{formatNumberWithCommas(metrics.nonZeroBids)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Bid Value</span>
									<span className="info-value">
										{formatCurrency(metrics.nonZeroBidsValueInUsd / 1000000)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Unique IPs</span>
									<span className="info-value">
										{formatNumberWithCommas(metrics.uniqueIpsWithRequests)}
									</span>
								</div>
							</div>
						</div>

						{/* Identity Resolution */}
						<div className="section">
							<h4>Identity Resolution</h4>
							<div className="metric-grid-4">
								<div className="id-card">
									<div className="id-count">
										{formatNumberWithCommas(metrics.idlXyWithRequests)}
									</div>
									<div className="id-label">IDL XY</div>
								</div>
								<div className="id-card">
									<div className="id-count">
										{formatNumberWithCommas(metrics.idlXiWithRequests)}
									</div>
									<div className="id-label">IDL XI</div>
								</div>
								<div className="id-card">
									<div className="id-count">
										{formatNumberWithCommas(metrics.idlXcWithRequests)}
									</div>
									<div className="id-label">IDL XC</div>
								</div>
								<div className="id-card">
									<div className="id-count">
										{formatNumberWithCommas(metrics.idlXmWithRequests)}
									</div>
									<div className="id-label">IDL XM</div>
								</div>
							</div>
						</div>

						{/* Technical Configuration */}
						<div className="section">
							<h4>Technical Configuration</h4>
							<div className="info-grid">
								<div className="info-item full">
									<span className="info-label">Export Targets</span>
									<div className="tag-list">
										{vector.exportTargets
											?.replace(/[[\]"]/g, "")
											.split(",")
											.map((target, index) => (
												<span key={index} className="tag">
													{target.trim()}
												</span>
											))}
									</div>
								</div>
								<div className="info-item full">
									<span className="info-label">Technical Attributes</span>
									<div className="tag-list">
										{vector.technicalAttributes
											?.split(",")
											.map((attr, index) => (
												<span key={index} className="tag">
													{attr.trim()}
												</span>
											))}
									</div>
								</div>
							</div>
						</div>

						{/* Deal Information */}
						<div className="section">
							<h4>Deal Information</h4>
							<div className="deal-summary">
								<div className="deal-count">
									<span className="count">{metrics.dealIds?.length || 0}</span>
									<span className="label">Active Deals</span>
								</div>
								<div className="deal-performance">
									<span className="label">Non-Zero Bids</span>
									<span className="value">
										{formatNumberWithCommas(metrics.nonZeroBids)}
									</span>
								</div>
							</div>
						</div>
					</div>
				);

			// 🆕 SEGMENTS TAB - Table view for all segment data
			case "segments":
				return (
					<div className="tab-content">
						{/* Loading State */}
						{segmentLoading && (
							<div
								className="loading-state"
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "60px 20px",
									textAlign: "center",
									backgroundColor: "#f8f9fa",
									borderRadius: "8px",
									border: "1px solid #e9ecef",
								}}
							>
								<div
									style={{
										width: "32px",
										height: "32px",
										border: "3px solid #f3f3f3",
										borderTop: "3px solid #0076BD",
										borderRadius: "50%",
										animation: "spin 1s linear infinite",
										marginBottom: "16px",
									}}
								></div>
								<p style={{ margin: 0, color: "#495057", fontSize: "14px" }}>
									Fetching segment data...
								</p>
							</div>
						)}

						{/* Error State */}
						{segmentError && !segmentLoading && (
							<div
								className="error-state"
								style={{
									padding: "20px",
									backgroundColor: "#fff5f5",
									border: "1px solid #fed7d7",
									borderRadius: "8px",
									textAlign: "center",
								}}
							>
								<div style={{ fontSize: "24px", marginBottom: "12px" }}>⚠️</div>
								<h4
									style={{
										margin: "0 0 8px 0",
										color: "#e53e3e",
										fontSize: "16px",
									}}
								>
									Error Loading Segments
								</h4>
								<p
									style={{
										margin: "0 0 16px 0",
										color: "#c53030",
										fontSize: "14px",
									}}
								>
									{segmentError}
								</p>
								<button
									onClick={fetchSegmentData}
									style={{
										backgroundColor: "#e53e3e",
										color: "white",
										border: "none",
										padding: "8px 16px",
										borderRadius: "6px",
										fontSize: "12px",
										fontWeight: "600",
										cursor: "pointer",
									}}
								>
									Retry
								</button>
							</div>
						)}

						{/* Segment Data Display */}
						{segmentData && !segmentLoading && !segmentError && (
							<>
								{/* Summary Statistics */}
								<div className="section">
									<h4>Summary</h4>
									<div className="metric-grid-4">
										<div className="metric-card">
											<div className="metric-value">
												{Array.isArray(segmentData) ? segmentData.length : 0}
											</div>
											<div className="metric-label">Total Segments</div>
										</div>
									</div>
								</div>

								{/* Segments Table */}
								<div className="section">
									<h4>
										Segment Details (
										{Array.isArray(segmentData) ? segmentData.length : 0})
									</h4>

									{Array.isArray(segmentData) && segmentData.length > 0 ? (
										<div
											className="segments-table-container"
											style={{
												overflowX: "auto",
												backgroundColor: "white",
												border: "1px solid #dee2e6",
												borderRadius: "8px",
												boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
											}}
										>
											<table
												className="segments-table"
												style={{
													width: "100%",
													borderCollapse: "collapse",
													fontSize: "12px",
													minWidth: "1200px",
												}}
											>
												<thead>
													<tr style={{ backgroundColor: "#f8f9fa" }}>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Segment ID
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Segment Name
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Account
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Type
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Description
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Category L1
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Category L2
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "left",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Status
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Reach
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Report Months
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Unique Users
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Avg eCPM
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Clicks
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Impressions
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Fills
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Start First
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Complete First
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Viewables
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Spend (USD)
														</th>
														<th
															style={{
																padding: "12px 8px",
																borderBottom: "2px solid #dee2e6",
																textAlign: "right",
																fontWeight: "600",
																color: "#495057",
																fontSize: "11px",
																textTransform: "uppercase",
															}}
														>
															Avg Spend/User
														</th>
													</tr>
												</thead>
												<tbody>
													{segmentData.map((segment, index) => (
														<tr
															key={index}
															style={{
																borderBottom: "1px solid #e9ecef",
																backgroundColor:
																	index % 2 === 0 ? "#ffffff" : "#f8f9fa",
															}}
														>
															<td
																style={{
																	padding: "10px 8px",
																	fontFamily: "Monaco, Menlo, monospace",
																	fontWeight: "600",
																	color: "#0076BD",
																}}
															>
																{segment.segmentId || "N/A"}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	fontWeight: "500",
																	color: "#212529",
																	maxWidth: "150px",
																	wordBreak: "break-word",
																}}
															>
																{segment.segmentName || "N/A"}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	color: "#495057",
																}}
															>
																{segment.accountName || "N/A"}
															</td>
															<td style={{ padding: "10px 8px" }}>
																<span
																	style={{
																		padding: "3px 8px",
																		borderRadius: "12px",
																		fontSize: "10px",
																		fontWeight: "600",
																		backgroundColor:
																			segment.audienceType === "lookalike"
																				? "#e3f2fd"
																				: "#f3e5f5",
																		color:
																			segment.audienceType === "lookalike"
																				? "#1565c0"
																				: "#7b1fa2",
																	}}
																>
																	{segment.audienceType || "N/A"}
																</span>
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	color: "#495057",
																	maxWidth: "120px",
																	position: "relative",
																}}
															>
																<div
																	style={{
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		cursor: segment.description
																			? "help"
																			: "default",
																	}}
																	title={segment.description || "N/A"}
																>
																	{segment.description || "N/A"}
																</div>
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	color: "#495057",
																}}
															>
																{segment.categoryL1 || "N/A"}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	color: "#495057",
																}}
															>
																{segment.categoryL2 || "N/A"}
															</td>
															<td style={{ padding: "10px 8px" }}>
																<span
																	style={{
																		padding: "3px 8px",
																		borderRadius: "12px",
																		fontSize: "10px",
																		fontWeight: "600",
																		backgroundColor:
																			segment.status === "active"
																				? "#e8f5e8"
																				: "#fff3cd",
																		color:
																			segment.status === "active"
																				? "#2e7d32"
																				: "#856404",
																	}}
																>
																	{segment.status || "N/A"}
																</span>
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	fontWeight: "600",
																	color: "#212529",
																}}
															>
																{formatNumberWithCommas(segment.reach)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																	fontSize: "11px",
																}}
															>
																{segment.reportMonths
																	? segment.reportMonths
																			.toString()
																			.replace(/,/g, ", ")
																	: "N/A"}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	fontWeight: "600",
																	color: "#28a745",
																}}
															>
																{formatNumberWithCommas(segment.uniqueUsers)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	fontWeight: "600",
																	color: "#007bff",
																}}
															>
																{formatCurrency(segment.avgEcpm)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(segment.clicks)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(
																	segment.weightedCprMarketImpressions,
																)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(segment.fills)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(
																	segment.startFirstOccurrence,
																)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(
																	segment.completeFirstOccurrence,
																)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatNumberWithCommas(segment.viewables)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	fontWeight: "600",
																	color: "#dc3545",
																}}
															>
																{formatCurrency(segment.spendInUsd / 1000000)}
															</td>
															<td
																style={{
																	padding: "10px 8px",
																	textAlign: "right",
																	color: "#495057",
																}}
															>
																{formatCurrency(segment.avgSpendPerUniqueUser)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<div
											className="empty-state"
											style={{
												padding: "40px 20px",
												textAlign: "center",
												backgroundColor: "#f8f9fa",
												borderRadius: "8px",
												border: "1px solid #e9ecef",
											}}
										>
											<div style={{ fontSize: "48px", marginBottom: "16px" }}>
												🧩
											</div>
											<h4 style={{ margin: "0 0 8px 0", color: "#495057" }}>
												No Segments Found
											</h4>
											<p
												style={{
													margin: 0,
													color: "#6c757d",
													fontSize: "14px",
												}}
											>
												No segment data is associated with this audience.
											</p>
										</div>
									)}
								</div>
							</>
						)}

						{/* Initial State - No data loaded yet */}
						{!segmentData && !segmentLoading && !segmentError && (
							<div
								className="initial-state"
								style={{
									padding: "60px 20px",
									textAlign: "center",
									backgroundColor: "#f8f9fa",
									borderRadius: "8px",
									border: "1px solid #e9ecef",
								}}
							>
								<div style={{ fontSize: "48px", marginBottom: "16px" }}>🧩</div>
								<h4 style={{ margin: "0 0 8px 0", color: "#495057" }}>
									Segment Data
								</h4>
								<p
									style={{
										margin: "0 0 20px 0",
										color: "#6c757d",
										fontSize: "14px",
									}}
								>
									Click the Refresh button to load segment data for this
									audience.
								</p>
								<button
									onClick={fetchSegmentData}
									style={{
										backgroundColor: "#0076BD",
										color: "white",
										border: "none",
										padding: "10px 20px",
										borderRadius: "6px",
										fontSize: "14px",
										fontWeight: "600",
										cursor: "pointer",
										transition: "all 0.2s ease",
									}}
									onMouseEnter={(e) => {
										e.target.style.backgroundColor = "#005A94";
									}}
									onMouseLeave={(e) => {
										e.target.style.backgroundColor = "#0076BD";
									}}
								>
									Load Segments
								</button>
							</div>
						)}
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className="modal-overlay">
			<div className="modal-container">
				{/* Header */}
				<div className="modal-header">
					<div className="header-content">
						<div>
							<h2 className="modal-title">
								{audience.audienceVector?.cleanAudienceName ||
									audience.audienceVector?.audienceName}
							</h2>
							<p className="modal-subtitle">
								{audience.audienceVector?.accountName} •{" "}
								{audience.audienceVector?.region}
							</p>
						</div>
						<button onClick={onClose} className="close-button">
							×
						</button>
					</div>
				</div>

				{/* Tab Navigation */}
				<div className="tab-navigation">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id)}
							className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
						>
							<span className="tab-icon">{tab.icon}</span>
							<span className="tab-label">{tab.label}</span>
						</button>
					))}
				</div>

				{/* Tab Content */}
				<div className="modal-body">{renderTabContent()}</div>

				<style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .modal-container {
            background: white;
            border-radius: 12px;
            width: 900px;
            height: 650px;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          }

          .modal-header {
            background: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
            padding: 20px 24px;
          }

          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .modal-title {
            margin: 0 0 6px 0;
            font-size: 20px;
            font-weight: 600;
            color: #212529;
            line-height: 1.3;
          }

          .modal-subtitle {
            margin: 0;
            font-size: 14px;
            color: #6c757d;
          }

          .close-button {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            color: #6c757d;
            font-size: 20px;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }

          .close-button:hover {
            background: #e9ecef;
            color: #495057;
          }

          .tab-navigation {
            display: flex;
            background: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
          }

          .tab-button {
            flex: 1;
            padding: 14px 16px;
            border: none;
            background: transparent;
            color: #6c757d;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s ease;
            border-bottom: 2px solid transparent;
          }

          .tab-button:hover {
            background: #f0f0f0;
            color: #495057;
          }

          .tab-button.active {
            background: white;
            color: #495057;
            border-bottom-color: #495057;
            font-weight: 600;
          }

          .tab-icon {
            font-size: 16px;
          }

          .modal-body {
            flex: 1;
            overflow-y: auto;
          }

          .tab-content {
            padding: 20px 24px;
          }

          .section {
            margin-bottom: 24px;
          }

          .section:last-child {
            margin-bottom: 0;
          }

          .section h4 {
            margin: 0 0 12px 0;
            font-size: 15px;
            font-weight: 600;
            color: #495057;
            padding-bottom: 6px;
            border-bottom: 1px solid #e9ecef;
          }

          /* Score Tags */
          .score-tag {
            background: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 16px;
            padding: 4px 12px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
          }

          .score-label {
            color: #1565c0;
            font-weight: 600;
          }

          .score-value {
            color: #0d47a1;
            font-weight: 700;
          }

          /* Metric Grids */
          .metric-grid-5 {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 8px;
          }

          .metric-grid-4 {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }

          .metric-grid-3 {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }

          .metric-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            text-align: center;
          }

          .metric-value {
            font-size: 16px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 4px;
            line-height: 1.2;
          }

          .metric-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          /* Metric Groups */
          .metric-group {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 14px;
          }

          .group-title {
            font-size: 13px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .metric-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6px 0;
            border-bottom: 1px solid #f1f3f4;
            font-size: 13px;
          }

          .metric-row:last-child {
            border-bottom: none;
          }

          .metric-row span:first-child {
            color: #6c757d;
            font-weight: 500;
          }

          .metric-row span:last-child {
            color: #212529;
            font-weight: 600;
          }

          /* Device Cards */
          .device-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            text-align: center;
          }

          .device-icon {
            font-size: 20px;
            margin-bottom: 6px;
          }

          .device-count {
            font-size: 14px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 2px;
          }

          .device-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
          }

          /* Info Grid */
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .info-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 10px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
          }

          .info-item.full {
            grid-column: 1 / -1;
          }

          .info-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .info-value {
            font-size: 13px;
            color: #212529;
            font-weight: 500;
            word-break: break-word;
          }

          .info-value.code {
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 11px;
            background: #f1f3f4;
            padding: 2px 6px;
            border-radius: 3px;
          }

          .description {
            grid-column: 1 / -1;
            margin-top: 8px;
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
          }

          .description p {
            margin: 6px 0 0 0;
            font-size: 13px;
            color: #495057;
            line-height: 1.4;
          }

          /* ID Cards */
          .id-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            text-align: center;
          }

          .id-count {
            font-size: 14px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 4px;
          }

          .id-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 600;
            text-transform: uppercase;
          }

          /* Deal Section */
          .deal-summary {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
          }

          .deal-count .count {
            font-size: 18px;
            font-weight: 700;
            color: #212529;
            display: block;
          }

          .deal-count .label,
          .deal-performance .label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 600;
            text-transform: uppercase;
          }

          .deal-performance .value {
            font-size: 14px;
            font-weight: 600;
            color: #212529;
          }

          /* Tags */
          .tag-list {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-top: 4px;
          }

          .tag {
            background: #e8f5e8;
            color: #2e7d32;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
          }

          /* Segment-specific styles */
          .segment-ids-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 12px;
            max-height: 400px;
            overflow-y: auto;
            padding: 8px;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #e9ecef;
          }

          .segment-id-item {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: all 0.2s ease;
            position: relative;
          }

          .segment-id-item:hover {
            border-color: #0076BD;
            box-shadow: 0 2px 8px rgba(0, 118, 189, 0.1);
            transform: translateY(-1px);
          }

          .segment-id-code {
            font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
            font-size: 12px;
            background: #f1f3f4;
            padding: 6px 8px;
            border-radius: 4px;
            color: #495057;
            font-weight: 600;
            border: 1px solid #dee2e6;
            flex: 1;
            margin-right: 8px;
            word-break: break-all;
            line-height: 1.2;
          }

          .segment-id-number {
            font-size: 10px;
            color: #6c757d;
            background: #e9ecef;
            padding: 4px 6px;
            border-radius: 12px;
            font-weight: 600;
            min-width: 24px;
            text-align: center;
            flex-shrink: 0;
          }

          /* Loading animation */
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Responsive Design */
          @media (max-width: 1024px) {
            .modal-container {
              width: 95vw;
              height: 85vh;
            }

            .metric-grid-4 {
              grid-template-columns: repeat(2, 1fr);
            }

            .metric-grid-3 {
              grid-template-columns: 1fr;
            }

            .info-grid {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 768px) {
            .modal-container {
              width: 98vw;
              height: 90vh;
            }

            .metric-grid-4 {
              grid-template-columns: 1fr;
            }

            .tab-content {
              padding: 16px 20px;
            }
          }
        `}</style>
			</div>
		</div>
	);
};

// Simple Device Distribution Chart Component
const DeviceChart = ({ audience }) => {
	const deviceData = {
		mobile: audience.audienceMetrics?.mobileDevices || 0,
		desktop: audience.audienceMetrics?.desktopsWithRequests || 0,
		tablet: audience.audienceMetrics?.tabletDevices || 0,
		ctv: audience.audienceMetrics?.connectedTvDevices || 0,
	};

	const total = Object.values(deviceData).reduce((sum, val) => sum + val, 0);

	if (total === 0) {
		return (
			<div
				style={{
					width: "80px",
					height: "80px",
					borderRadius: "50%",
					backgroundColor: "#f8f9fa",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: "24px",
					color: "#dee2e6",
					userSelect: "none",
				}}
			>
				—
			</div>
		);
	}

	const mobilePercent = (deviceData.mobile / total) * 100;
	const desktopPercent = (deviceData.desktop / total) * 100;
	const tabletPercent = (deviceData.tablet / total) * 100;
	const ctvPercent = (deviceData.ctv / total) * 100;

	const colors = {
		mobile: "#4CAF50", // Green
		desktop: "#2196F3", // Blue
		tablet: "#FF9800", // Orange
		ctv: "#9C27B0", // Purple
	};

	return (
		<div
			style={{
				position: "relative",
				width: "80px",
				height: "80px",
				userSelect: "none",
			}}
		>
			<svg
				width="80"
				height="80"
				viewBox="0 0 42 42"
				style={{ transform: "rotate(-90deg)" }}
			>
				<circle
					cx="21"
					cy="21"
					r="15.915"
					fill="transparent"
					stroke="#f1f1f1"
					strokeWidth="3"
				/>
				<circle
					cx="21"
					cy="21"
					r="15.915"
					fill="transparent"
					stroke={colors.mobile}
					strokeWidth="3"
					strokeDasharray={`${mobilePercent} ${100 - mobilePercent}`}
					strokeDashoffset="0"
				/>
				<circle
					cx="21"
					cy="21"
					r="15.915"
					fill="transparent"
					stroke={colors.desktop}
					strokeWidth="3"
					strokeDasharray={`${desktopPercent} ${100 - desktopPercent}`}
					strokeDashoffset={`-${mobilePercent}`}
				/>
				<circle
					cx="21"
					cy="21"
					r="15.915"
					fill="transparent"
					stroke={colors.tablet}
					strokeWidth="3"
					strokeDasharray={`${tabletPercent} ${100 - tabletPercent}`}
					strokeDashoffset={`-${mobilePercent + desktopPercent}`}
				/>
				<circle
					cx="21"
					cy="21"
					r="15.915"
					fill="transparent"
					stroke={colors.ctv}
					strokeWidth="3"
					strokeDasharray={`${ctvPercent} ${100 - ctvPercent}`}
					strokeDashoffset={`-${mobilePercent + desktopPercent + tabletPercent}`}
				/>
			</svg>
			<div
				style={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					fontSize: "14px",
					fontWeight: "bold",
					color: "#333",
					userSelect: "none",
				}}
			>
				100
			</div>
		</div>
	);
};

// Simple Audience Tile Component - FIXED to prevent highlighting
const SimpleAudienceTile = ({ audience, onTileClick, categoryInfo }) => {
	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
			if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
			return value.toLocaleString();
		}
		if (typeof value === "string" && !isNaN(value)) {
			const num = parseInt(value);
			if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
			if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
			return num.toLocaleString();
		}
		return value;
	};

	const getCTRPercent = () => {
		const ctr = audience.audienceMetrics?.clickThroughRate || 0;
		return Math.min(ctr, 25); // Cap at 25% for display
	};

	const getViewabilityPercent = () => {
		return audience.audienceMetrics?.viewability || 0;
	};

	const getCategoryType = () => {
		if (audience.audienceVector?.dataSource === "OpenAudience")
			return "first-party";
		if (audience.audienceVector?.type === "lookalike") return "lookalike";
		return "third-party";
	};

	const getCategoryColor = (type) => {
		switch (type) {
			case "first-party":
				return "#28a745";
			case "lookalike":
				return "#6f42c1";
			case "third-party":
				return "#17a2b8";
			default:
				return "#6c757d";
		}
	};

	const categoryType = getCategoryType();
	const ctrPercent = getCTRPercent();
	const viewabilityPercent = getViewabilityPercent();

	return (
		<div
			onClick={() => onTileClick(audience)}
			style={{
				backgroundColor: "white",
				borderRadius: "12px",
				padding: "24px",
				boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
				cursor: "pointer",
				transition: "all 0.2s ease",
				border: "1px solid #e9ecef",
				height: "fit-content",
				flexShrink: 0,
				width: "320px",
				// 🔧 FIXED: Prevent text selection and highlighting
				userSelect: "none",
				WebkitUserSelect: "none",
				MozUserSelect: "none",
				msUserSelect: "none",
				WebkitTouchCallout: "none",
				WebkitTapHighlightColor: "transparent",
				outline: "none",
				// Prevent focus highlight
				WebkitFocusRingColor: "transparent",
			}}
			onMouseEnter={(e) => {
				e.currentTarget.style.transform = "translateY(-4px)";
				e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
			}}
			onMouseLeave={(e) => {
				e.currentTarget.style.transform = "translateY(0)";
				e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
			}}
			// 🔧 FIXED: Prevent focus states
			onFocus={(e) => {
				e.currentTarget.style.outline = "none";
			}}
			tabIndex={0}
		>
			{/* Header with relevance score and category */}
			<div
				style={{
					marginBottom: "16px",
					userSelect: "none",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						marginBottom: "8px",
					}}
				>
					<div
						style={{
							backgroundColor: categoryInfo.color,
							color: "white",
							padding: "6px 12px",
							borderRadius: "16px",
							fontSize: "12px",
							fontWeight: "600",
							userSelect: "none",
						}}
					>
						{audience.relevanceScore
							? `${audience.relevanceScore.toFixed(0)}% Match`
							: "N/A Match"}
					</div>
				</div>

				{/* Category Type and Status in Same Row */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "12px",
						userSelect: "none",
					}}
				>
					<div
						style={{
							backgroundColor: getCategoryColor(categoryType),
							color: "white",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "10px",
							fontWeight: "600",
							textTransform: "uppercase",
							display: "inline-block",
						}}
					>
						{categoryType}
					</div>

					<div
						style={{
							backgroundColor: getCategoryColor(categoryType),
							color: "white",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "10px",
							fontWeight: "600",
							textTransform: "uppercase",
							display: "inline-block",
						}}
					>
						{audience.audienceVector?.status || "N/A"}
					</div>
				</div>
			</div>

			{/* Title and Company */}
			<div
				style={{
					marginBottom: "16px",
					userSelect: "none",
				}}
			>
				<h3
					style={{
						margin: "0 0 4px 0",
						fontSize: "18px",
						fontWeight: "700",
						color: "#212529",
						lineHeight: "1.3",
						userSelect: "none",
					}}
				>
					{audience.audienceVector?.cleanAudienceName ||
						audience.audienceVector?.audienceName}
				</h3>
				<p
					style={{
						margin: "0 0 8px 0",
						fontSize: "14px",
						color: "#6c757d",
						fontWeight: "500",
						userSelect: "none",
					}}
				>
					{audience.audienceVector?.accountName}
				</p>
				<p
					style={{
						margin: 0,
						fontSize: "13px",
						color: "#495057",
						lineHeight: "1.4",
						userSelect: "none",
					}}
				>
					{audience.audienceVector?.notesDescription ||
						`${audience.audienceVector?.audienceStrategy} strategy targeting ${audience.audienceVector?.region} region`}
				</p>
			</div>

			{/* Audience Size and Relevance Score */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: "16px",
					marginBottom: "16px",
					userSelect: "none",
				}}
			>
				<div>
					<div
						style={{
							fontSize: "12px",
							color: "#6c757d",
							marginBottom: "4px",
							userSelect: "none",
						}}
					>
						Reach
					</div>
					<div
						style={{
							fontSize: "16px",
							fontWeight: "bold",
							color: "#28a745",
							display: "flex",
							alignItems: "center",
							gap: "4px",
							userSelect: "none",
						}}
					>
						{formatNumber(audience.audienceMetrics?.uniqueUsers)}
					</div>
				</div>
				<div>
					<div
						style={{
							fontSize: "12px",
							color: "#6c757d",
							marginBottom: "4px",
							userSelect: "none",
						}}
					>
						Total Spend
					</div>
					<div
						style={{
							fontSize: "16px",
							fontWeight: "bold",
							color: "#495057",
							userSelect: "none",
						}}
					>
						{formatCurrency(audience.audienceMetrics?.spendInUsd / 1000000)}
					</div>
				</div>
				<div>
					<div
						style={{
							fontSize: "12px",
							color: "#6c757d",
							marginBottom: "4px",
							userSelect: "none",
						}}
					>
						Timeline
					</div>
					<div
						style={{
							fontSize: "16px",
							fontWeight: "bold",
							color: "#226fbcff",
							userSelect: "none",
						}}
					>
						{audience.audienceMetrics?.reportMonths} Months
					</div>
				</div>
			</div>

			{/* Performance Section */}
			<div
				style={{
					marginBottom: "20px",
					userSelect: "none",
				}}
			>
				<div
					style={{
						fontSize: "14px",
						fontWeight: "600",
						color: "#495057",
						marginBottom: "12px",
						userSelect: "none",
					}}
				>
					Performance
				</div>

				{/* CTR */}
				<div style={{ marginBottom: "8px" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: "4px",
						}}
					>
						<span
							style={{
								fontSize: "12px",
								color: "#6c757d",
								userSelect: "none",
							}}
						>
							CTR
						</span>
						<span
							style={{
								fontSize: "12px",
								fontWeight: "600",
								color: "#495057",
								userSelect: "none",
							}}
						>
							{ctrPercent.toFixed(2)}%
						</span>
					</div>
					<div
						style={{
							width: "100%",
							height: "6px",
							backgroundColor: "#e9ecef",
							borderRadius: "3px",
							overflow: "hidden",
							userSelect: "none",
						}}
					>
						<div
							style={{
								width: `${Math.min((ctrPercent / 25) * 100, 100)}%`,
								height: "100%",
								backgroundColor: "#2196F3",
								borderRadius: "3px",
								userSelect: "none",
							}}
						/>
					</div>
				</div>

				{/* Viewability */}
				<div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: "4px",
						}}
					>
						<span
							style={{
								fontSize: "12px",
								color: "#6c757d",
								userSelect: "none",
							}}
						>
							Viewability
						</span>
						<span
							style={{
								fontSize: "12px",
								fontWeight: "600",
								color: "#495057",
								userSelect: "none",
							}}
						>
							{viewabilityPercent.toFixed(0)}%
						</span>
					</div>
					<div
						style={{
							width: "100%",
							height: "6px",
							backgroundColor: "#e9ecef",
							borderRadius: "3px",
							overflow: "hidden",
							userSelect: "none",
						}}
					>
						<div
							style={{
								width: `${Math.min(viewabilityPercent, 100)}%`,
								height: "100%",
								backgroundColor: "#4CAF50",
								borderRadius: "3px",
								userSelect: "none",
							}}
						/>
					</div>
				</div>
			</div>

			{/* Device Distribution */}
			<div
				style={{
					marginBottom: "20px",
					userSelect: "none",
				}}
			>
				<div
					style={{
						fontSize: "14px",
						fontWeight: "600",
						color: "#495057",
						marginBottom: "12px",
						userSelect: "none",
					}}
				>
					Device Distribution
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						userSelect: "none",
					}}
				>
					<DeviceChart audience={audience} />
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						flexWrap: "wrap",
						gap: "12px",
						marginTop: "8px",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						<div
							style={{
								width: "10px",
								height: "10px",
								backgroundColor: "#4CAF50",
								borderRadius: "50%",
							}}
						></div>
						<span style={{ fontSize: "11px", color: "#495057" }}>Mobile</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						<div
							style={{
								width: "10px",
								height: "10px",
								backgroundColor: "#2196F3",
								borderRadius: "50%",
							}}
						></div>
						<span style={{ fontSize: "11px", color: "#495057" }}>Desktop</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						<div
							style={{
								width: "10px",
								height: "10px",
								backgroundColor: "#FF9800",
								borderRadius: "50%",
							}}
						></div>
						<span style={{ fontSize: "11px", color: "#495057" }}>Tablet</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
						<div
							style={{
								width: "10px",
								height: "10px",
								backgroundColor: "#9C27B0",
								borderRadius: "50%",
							}}
						></div>
						<span style={{ fontSize: "11px", color: "#495057" }}>CTV</span>
					</div>
				</div>
			</div>

			{/* Tags */}
			<div
				style={{
					marginBottom: "20px",
					userSelect: "none",
				}}
			>
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: "6px",
					}}
				>
					<span
						style={{
							backgroundColor: "#f8f9fa",
							color: "#495057",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "11px",
							fontWeight: "500",
							userSelect: "none",
						}}
					>
						{audience.audienceVector?.region}
					</span>
					<span
						style={{
							backgroundColor: "#f8f9fa",
							color: "#495057",
							padding: "4px 8px",
							borderRadius: "4px",
							fontSize: "11px",
							fontWeight: "500",
							userSelect: "none",
						}}
					>
						{audience.audienceVector?.audienceStrategy}
					</span>
					{audience.audienceVector?.primaryPersona && (
						<span
							style={{
								backgroundColor: "#f8f9fa",
								color: "#495057",
								padding: "4px 8px",
								borderRadius: "4px",
								fontSize: "11px",
								fontWeight: "500",
								userSelect: "none",
							}}
						>
							{audience.audienceVector?.primaryPersona}
						</span>
					)}
				</div>
			</div>

			{/* Footer */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					paddingTop: "16px",
					borderTop: "1px solid #e9ecef",
					userSelect: "none",
				}}
			>
				<button
					style={{
						backgroundColor: "transparent",
						color: "#0076BD",
						border: "1px solid #0076BD",
						padding: "6px 12px",
						borderRadius: "4px",
						fontSize: "12px",
						fontWeight: "500",
						cursor: "pointer",
						transition: "all 0.2s ease",
						userSelect: "none",
						outline: "none",
					}}
					onMouseEnter={(e) => {
						e.target.style.backgroundColor = "#0076BD";
						e.target.style.color = "white";
					}}
					onMouseLeave={(e) => {
						e.target.style.backgroundColor = "transparent";
						e.target.style.color = "#0076BD";
					}}
					// 🔧 FIXED: Prevent event propagation on button click
					onClick={(e) => {
						e.stopPropagation();
						onTileClick(audience);
					}}
				>
					View Details
				</button>
			</div>
		</div>
	);
};

// Category Section Component with Independent Scrolling and Sort Functionality
const CategorySectionWithScroll = ({
	categoryKey,
	allAudiences,
	onTileClick,
	showInitialTop3 = true,
}) => {
	const [showAll, setShowAll] = useState(false);
	const [scrollContainerRef, setScrollContainerRef] = useState(null);
	const [sortBy, setSortBy] = useState("compositeScore"); // Default sort by composite score

	const categoryInfo = getCategoryInfo(categoryKey);

	// Sort options configuration
	const sortOptions = [
		{
			value: "relevanceScore",
			label: "Relevance Score",
			desc: "Best match for your criteria",
		},
		{
			value: "compositeScore",
			label: "Composite Score",
			desc: "Overall performance rating",
		},
		{
			value: "uniqueUsers",
			label: "Audience Size",
			desc: "Number of unique users",
		},
		{
			value: "clickThroughRate",
			label: "Click-Through Rate",
			desc: "Engagement performance",
		},
		{
			value: "viewability",
			label: "Viewability",
			desc: "Ad viewability percentage",
		},
		{ value: "spendInUsd", label: "Total Spend", desc: "Total spend amount" },
		{
			value: "avgEcpm",
			label: "Average eCPM",
			desc: "Revenue per thousand impressions",
		},
		{
			value: "requests",
			label: "Total Requests",
			desc: "Number of ad requests",
		},
	];

	// Sorting function
	const getSortValue = (audience, sortKey) => {
		switch (sortKey) {
			case "relevanceScore":
				return audience.relevanceScore || 0;
			case "compositeScore":
				return audience.compositeScore || 0;
			case "uniqueUsers":
				return audience.audienceMetrics?.uniqueUsers || 0;
			case "clickThroughRate":
				return audience.audienceMetrics?.clickThroughRate || 0;
			case "viewability":
				return audience.audienceMetrics?.viewability || 0;
			case "spendInUsd":
				return audience.audienceMetrics?.spendInUsd || 0;
			case "avgEcpm":
				return audience.audienceMetrics?.avgEcpm || 0;
			case "requests":
				return audience.audienceMetrics?.requests || 0;
			default:
				return 0;
		}
	};

	// Get provider and organization status
	const getProviderOrganizationStatus = (key) => {
		switch (key) {
			case "non_oa_non_oa":
				return { provider: "OAA", organization: "OAA" };
			case "oa_provider_non_oa_org":
				return { provider: "OA", organization: "OAA" };
			case "oa_provider_oa_org":
				return { provider: "OA", organization: "OA" };
			default:
				return { provider: "OAA", organization: "OA" };
		}
	};

	const status = getProviderOrganizationStatus(categoryKey);

	if (allAudiences.length === 0) return null;

	// Sort audiences based on selected criteria
	const sortedAudiences = [...allAudiences].sort((a, b) => {
		const aValue = getSortValue(a, sortBy);
		const bValue = getSortValue(b, sortBy);
		return bValue - aValue; // Descending order (highest first)
	});
	const top3Audiences = sortedAudiences.slice(0, 3);
	const displayAudiences = showAll ? sortedAudiences : top3Audiences;

	// Get current sort option details
	const currentSortOption =
		sortOptions.find((opt) => opt.value === sortBy) || sortOptions[0];

	const scrollLeft = () => {
		if (scrollContainerRef) {
			scrollContainerRef.scrollLeft -= 340;
		}
	};

	const scrollRight = () => {
		if (scrollContainerRef) {
			scrollContainerRef.scrollLeft += 340;
		}
	};

	return (
		<div
			style={{
				marginBottom: "48px",
				userSelect: "none",
			}}
		>
			{/* Category Header */}
			<div
				style={{
					marginBottom: "24px",
					padding: "16px 20px",
					backgroundColor: categoryInfo.bgColor,
					border: `2px solid ${categoryInfo.borderColor}`,
					borderRadius: "8px",
					userSelect: "none",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
					}}
				>
					<div>
						<h3
							style={{
								margin: "0 0 8px 0",
								fontSize: "18px",
								fontWeight: "700",
								color: categoryInfo.color,
								userSelect: "none",
							}}
						>
							Provider: {status.provider} • Organization: {status.organization}
						</h3>
						<p
							style={{
								margin: 0,
								fontSize: "12px",
								color: "#495057",
								fontStyle: "italic",
								userSelect: "none",
							}}
						>
							Sorted by: {currentSortOption.label} • {currentSortOption.desc}
						</p>
					</div>

					{/* Controls */}
					<div
						className="category-header-controls"
						style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}
					>
						{/* Sort Dropdown */}
						<div
							style={{ display: "flex", flexDirection: "column", gap: "4px" }}
						>
							<label
								style={{
									fontSize: "11px",
									fontWeight: "600",
									color: categoryInfo.color,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
									userSelect: "none",
								}}
							>
								Sort By
							</label>
							<select
								value={sortBy}
								onChange={(e) => setSortBy(e.target.value)}
								style={{
									backgroundColor: "white",
									border: `2px solid ${categoryInfo.color}`,
									borderRadius: "6px",
									padding: "8px 12px",
									fontSize: "12px",
									fontWeight: "600",
									color: categoryInfo.color,
									cursor: "pointer",
									outline: "none",
									minWidth: "160px",
									userSelect: "none",
								}}
							>
								{sortOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						{/* Toggle Button */}
						{allAudiences.length > 3 && (
							<div
								style={{ display: "flex", flexDirection: "column", gap: "4px" }}
							>
								<label
									style={{
										fontSize: "11px",
										fontWeight: "600",
										color: categoryInfo.color,
										textTransform: "uppercase",
										letterSpacing: "0.5px",
										userSelect: "none",
									}}
								>
									View
								</label>
								<button
									onClick={() => setShowAll(!showAll)}
									style={{
										backgroundColor: showAll
											? categoryInfo.color
											: "transparent",
										color: showAll ? "white" : categoryInfo.color,
										border: `2px solid ${categoryInfo.color}`,
										padding: "8px 16px",
										borderRadius: "6px",
										fontSize: "12px",
										fontWeight: "600",
										cursor: "pointer",
										transition: "all 0.2s ease",
										textTransform: "uppercase",
										letterSpacing: "0.5px",
										userSelect: "none",
										outline: "none",
										whiteSpace: "nowrap",
									}}
									onMouseEnter={(e) => {
										if (!showAll) {
											e.target.style.backgroundColor = categoryInfo.color;
											e.target.style.color = "white";
										}
									}}
									onMouseLeave={(e) => {
										if (!showAll) {
											e.target.style.backgroundColor = "transparent";
											e.target.style.color = categoryInfo.color;
										}
									}}
								>
									{showAll ? "Top 3" : `All ${allAudiences.length}`}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Scrollable Container */}
			<div style={{ position: "relative" }}>
				{/* Scroll Controls - Only show when viewing all */}
				{showAll && allAudiences.length > 3 && (
					<div
						style={{
							position: "absolute",
							right: "0",
							top: "-60px",
							display: "flex",
							gap: "8px",
							zIndex: 10,
						}}
					>
						<button
							onClick={scrollLeft}
							style={{
								backgroundColor: "white",
								border: "1px solid #ddd",
								borderRadius: "50%",
								width: "36px",
								height: "36px",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: "16px",
								color: "#666",
								boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								transition: "all 0.2s ease",
								userSelect: "none",
								outline: "none",
							}}
							onMouseEnter={(e) => {
								e.target.style.backgroundColor = "#f0f0f0";
								e.target.style.color = "#333";
							}}
							onMouseLeave={(e) => {
								e.target.style.backgroundColor = "white";
								e.target.style.color = "#666";
							}}
						>
							←
						</button>
						<button
							onClick={scrollRight}
							style={{
								backgroundColor: "white",
								border: "1px solid #ddd",
								borderRadius: "50%",
								width: "36px",
								height: "36px",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: "16px",
								color: "#666",
								boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								transition: "all 0.2s ease",
								userSelect: "none",
								outline: "none",
							}}
							onMouseEnter={(e) => {
								e.target.style.backgroundColor = "#f0f0f0";
								e.target.style.color = "#333";
							}}
							onMouseLeave={(e) => {
								e.target.style.backgroundColor = "white";
								e.target.style.color = "#666";
							}}
						>
							→
						</button>
					</div>
				)}

				{/* Tiles Container */}
				{showAll ? (
					// Scrollable horizontal layout for all audiences
					<div
						style={{
							backgroundColor: "#f8f9fa",
							borderRadius: "12px",
							padding: "20px",
							overflow: "hidden",
						}}
					>
						<div
							ref={setScrollContainerRef}
							className="category-scroll-container"
							style={{
								display: "flex",
								gap: "20px",
								overflowX: "auto",
								overflowY: "hidden",
								paddingBottom: "20px",
								scrollBehavior: "smooth",
							}}
						>
							{displayAudiences.map((audience, index) => (
								<SimpleAudienceTile
									key={`${categoryKey}-all-${audience.id || index}`}
									audience={audience}
									onTileClick={onTileClick}
									categoryInfo={categoryInfo}
								/>
							))}
						</div>
					</div>
				) : (
					// Grid layout for top 3 audiences
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
							gap: "24px",
							maxWidth: "1200px",
						}}
					>
						{displayAudiences.map((audience, index) => (
							<SimpleAudienceTile
								key={`${categoryKey}-top3-${audience.id || index}`}
								audience={audience}
								onTileClick={onTileClick}
								categoryInfo={categoryInfo}
							/>
						))}
					</div>
				)}

				{/* Audience Count Indicator */}
				{showAll && (
					<div
						style={{
							marginTop: "16px",
							textAlign: "center",
							padding: "12px",
							backgroundColor: "white",
							borderRadius: "8px",
							border: "1px solid #e9ecef",
							fontSize: "14px",
							color: "#666",
							userSelect: "none",
						}}
					>
						Showing {displayAudiences.length} of {allAudiences.length} audiences
						in this category
					</div>
				)}
			</div>
		</div>
	);
};

// ✅ FIXED: Enhanced relevance score calculation
const calculateRelevanceScore = (audience) => {
	// Method 1: If there's already a relevance score, use it
	if (audience.relevanceScore && typeof audience.relevanceScore === "number") {
		return audience.relevanceScore;
	}

	// Method 2: If there's a score field, transform it
	if (typeof audience.score === "number") {
		// Audience sigmoid relevance curve (see constants.js)
		const score = parseFloat(audience.score);
		return relevanceFromDistance(score, "AUDIENCE");
	}
};

// Main Component
export const RelevanceAudienceResults = ({
	data,
	isLoading = false,
	error = null,
	allData = null,
	onOpenModal = null,
}) => {
	const [selectedAudience, setSelectedAudience] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	// ✅ DEBUG: Log what we're receiving
	console.log("🎯 RelevanceAudienceResults received:", {
		dataLength: data?.length || 0,
		allDataLength: allData?.length || 0,
		usingDataset: allData?.length > 0 ? "allData" : "data",
	});

	// ✅ FIXED: Always prioritize complete dataset (allData) over paginated data (data)
	const completeDataset = useMemo(() => {
		// ALWAYS use allData if it's provided and has data, regardless of what data contains
		if (allData && Array.isArray(allData) && allData.length > 0) {
			console.log(
				`✅ Using complete dataset with ${allData.length} items for categorization`,
			);
			return allData;
		}

		// Only fall back to data if allData is not provided
		if (Array.isArray(data) && data.length > 0) {
			console.log(`⚠️ Falling back to paginated data with ${data.length} items`);
			return data;
		}

		console.log("❌ No data available for categorization");
		return [];
	}, [data, allData]);

	// ✅ FIXED: Group audiences by category using the COMPLETE dataset
	const groupedAudiences = useMemo(() => {
		if (!Array.isArray(completeDataset) || completeDataset.length === 0) {
			console.log("❌ No complete dataset available for grouping");
			return {};
		}

		console.log(`📊 Grouping ${completeDataset.length} audiences by category`);
		const grouped = groupAudiencesByCategory(completeDataset);

		// Log the results
		Object.keys(grouped).forEach((categoryKey) => {
			console.log(
				`📋 Category ${categoryKey}: ${grouped[categoryKey].length} audiences`,
			);
		});

		return grouped;
	}, [completeDataset]);

	// ✅ FIXED: Calculate relevance scores for all audiences in each category
	const audiencesWithScoresByCategory = useMemo(() => {
		const result = {};

		Object.keys(groupedAudiences).forEach((categoryKey) => {
			const audiences = groupedAudiences[categoryKey];

			if (!Array.isArray(audiences) || audiences.length === 0) {
				result[categoryKey] = [];
				return;
			}

			// Calculate relevance scores for all audiences in this category
			const audiencesWithScores = audiences.map((audience) => {
				const relevanceScore = calculateRelevanceScore(audience);

				return {
					...audience,
					relevanceScore,
				};
			});

			result[categoryKey] = audiencesWithScores;
		});

		return result;
	}, [groupedAudiences]);

	// ✅ FIXED: Calculate totals for all 4 categories
	const totals = useMemo(() => {
		// All 4 categories:
		// 1. Provider: OAA, Organization: OAA
		// 2. Provider: OA, Organization: OAA
		// 3. Provider: OA, Organization: OA
		// 4. Provider: OAA, Organization: OA
		const requestedCategories = [
			"non_oa_non_oa",
			"oa_provider_non_oa_org",
			"oa_provider_oa_org",
			"non_oa_provider_oa_org",
		];

		const totalCount = requestedCategories.reduce(
			(sum, key) => sum + (audiencesWithScoresByCategory[key]?.length || 0),
			0,
		);

		// Always show all 4 categories, even if some are empty
		const categoryBreakdown = requestedCategories.map((key) => ({
			key,
			count: audiencesWithScoresByCategory[key]?.length || 0,
			info: getCategoryInfo(key),
		}));

		return {
			totalCount,
			visibleCategories: 4, // Always 4 categories
			categoryBreakdown,
		};
	}, [audiencesWithScoresByCategory]);

	const handleTileClick = (audience) => {
		setSelectedAudience(audience);
		setIsModalOpen(true);

		// ✅ NEW: Also notify parent if callback is provided
		if (onOpenModal) {
			onOpenModal(audience);
		}
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedAudience(null);
	};

	if (isLoading) {
		return (
			<div className="results-container">
				<div className="loading-state">
					<div className="loading-spinner"></div>
					<p>Loading audience results...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="results-container">
				<div className="error-state">
					<div className="error-icon">❌</div>
					<h3>Error Loading Results</h3>
					<p>{error}</p>
					<button
						onClick={() => window.location.reload()}
						className="retry-button"
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="results-container" style={{ userSelect: "none" }}>
			<div className="results-section">
				{/* Overall Header */}
				<div
					style={{
						marginBottom: "32px",
						userSelect: "none",
					}}
				>
					<h2 className="section-title">
						Audience Results by Category
						<span className="results-count">
							({totals.totalCount} total results)
						</span>
					</h2>
				</div>

				{/* Category Sections with Independent Scrolling */}
				<div>
					{[
						"non_oa_non_oa",
						"oa_provider_non_oa_org",
						"non_oa_provider_oa_org",
						"oa_provider_oa_org",
					].map((categoryKey) => (
						<CategorySectionWithScroll
							key={categoryKey}
							categoryKey={categoryKey}
							allAudiences={audiencesWithScoresByCategory[categoryKey] || []}
							onTileClick={handleTileClick}
						/>
					))}
				</div>

				{/* Overall Empty State - Only show if no data at all */}
				{totals.totalCount === 0 && !isLoading && (
					<div
						className="empty-results-content"
						style={{
							textAlign: "center",
							padding: "80px 20px",
							color: "#666",
							userSelect: "none",
						}}
					>
						<div style={{ fontSize: "64px", marginBottom: "20px" }}>📊</div>
						<h3>No Audience Data Available</h3>
						<p>
							No audience data found across all categories. Try running a search
							or check your data source.
						</p>
					</div>
				)}
			</div>

			{/* Modal */}
			<AudienceDetailModal
				audience={selectedAudience}
				isOpen={isModalOpen}
				onClose={handleCloseModal}
			/>

			{/* Styles */}
			<style jsx>{`
        /* 🔧 FIXED: Global text selection prevention */
        * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }

        /* Category Scroll Container Styles */
        .category-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: #ccc transparent;
          -webkit-user-select: none;
          user-select: none;
        }

        .category-scroll-container::-webkit-scrollbar {
          height: 8px;
        }

        .category-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .category-scroll-container::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        .category-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #999;
        }

        .results-section {
          overflow: visible;
          position: relative;
          z-index: 1;
          -webkit-user-select: none;
          user-select: none;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          font-size: 22px;
          font-weight: 700;
          color: #212529;
          -webkit-user-select: none;
          user-select: none;
        }

        .results-count {
          font-size: 16px;
          color: #666;
          font-weight: normal;
          -webkit-user-select: none;
          user-select: none;
        }

        .loading-state, .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
          -webkit-user-select: none;
          user-select: none;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0076BD;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 24px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .empty-results-content h3 {
          margin: 0 0 16px 0;
          font-size: 24px;
          color: #333;
          -webkit-user-select: none;
          user-select: none;
        }

        .empty-results-content p {
          margin: 0;
          color: #666;
          line-height: 1.6;
          font-size: 16px;
          -webkit-user-select: none;
          user-select: none;
        }

        /* 🔧 FIXED: Prevent any focus outlines */
        button:focus,
        div:focus,
        span:focus {
          outline: none !important;
          box-shadow: none !important;
        }

        /* 🔧 FIXED: Prevent text highlighting on hover */
        *::selection {
          background: transparent;
        }

        *::-moz-selection {
          background: transparent;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .category-scroll-container {
            gap: 16px !important;
          }
        }

        @media (max-width: 768px) {
          .section-title {
            font-size: 18px;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          /* Hide scroll buttons on mobile, rely on touch scrolling */
          .category-scroll-container + div button {
            display: none !important;
          }

          /* Mobile responsive for category header controls */
          .category-header-controls {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
          }

          .category-header-controls > div {
            flex-direction: row !important;
            justify-content: space-between !important;
          }

          .category-header-controls select,
          .category-header-controls button {
            min-width: auto !important;
            width: auto !important;
          }
        }

        @media (max-width: 480px) {
          /* Mobile responsive for scrollable tiles */
          .category-scroll-container > div {
            width: 280px !important;
          }
        }
      `}</style>
		</div>
	);
};
